import React from 'react';
import moment from 'moment';

import Button from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import Select from 'components/Select/Select';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';

import { getPayoutRequests, getPayoutRequestDetail, confirmPayoutRequest, deletePayoutRequest } from 'api/payoutrequests';
import ConfirmDeleteDialog from 'pages/PayoutRequest/components/ConfirmDeleteDialog';
import ImageViewer from 'components/ImageViewer/ImageViewer';
import PayoutMessageBox from 'components/PayoutMessageBox/PayoutMessageBox';

const PAGE_SIZE = 20;
const IMAGE_PROXY_URL = 'https://kot-image-proxy.coinflipsyndicate.online:8443/39bd83260b149297453aa06d71090d57';

const PayoutRequests: React.FC = () => {
    const queryClient = useQueryClient();
    const [selectType, setSelectType] = React.useState('pending');
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(PAGE_SIZE);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

    const { data, isLoading } = useQuery(['payoutrequests', selectType, page, pageSize], getPayoutRequests);

    const detailQuery = useQuery(['payoutrequest', selectedId], getPayoutRequestDetail, {
        enabled: !!selectedId,
    });

    const confirmMutation = useMutation(() => confirmPayoutRequest(selectedId!), {
        onSuccess: () => {
            queryClient.invalidateQueries(['payoutrequest', selectedId]);
            queryClient.invalidateQueries(['payoutrequests']);
        }
    });

    const deleteMutation = useMutation(() => deletePayoutRequest(selectedId!), {
        onSuccess: () => {
            queryClient.invalidateQueries(['payoutrequests']);
            setDeleteDialogOpen(false);
            setModalOpen(false);
        }
    });

    // Prefetch скриншотов для pending записей (скрины в detail → request_snapshot.rooms)
    const prefetchImages = React.useRef<HTMLImageElement[]>([]);
    React.useEffect(() => {
        if (selectType !== 'pending' || !data) return;
        const items = (data?.data ?? data ?? []).slice(0, 5);
        const images: HTMLImageElement[] = [];
        items.forEach(async (item: any) => {
            try {
                const detail = await queryClient.fetchQuery(
                    ['payoutrequest', String(item.id)],
                    getPayoutRequestDetail,
                    { staleTime: 5 * 60 * 1000 }
                );
                const rooms = detail?.request_snapshot?.rooms ?? [];
                rooms.forEach((room: any) => {
                    [room.screenshot, room.screenshot2].filter(Boolean).forEach((url: string) => {
                        const img = new Image();
                        img.src = `${IMAGE_PROXY_URL}/image?url=${encodeURIComponent(url)}`;
                        images.push(img);
                    });
                });
            } catch {}
        });
        prefetchImages.current = images;
    }, [data, selectType, queryClient]);

    const handleSelectChange = (value: string) => {
        setSelectType(value);
        setPage(0);
    };

    const goToDetail = ({ data }: any) => {
        setSelectedId(data.id);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const formatDate = ({ value }: { value: string }) => {
        return moment(value).format('DD MMM YYYY HH:mm');
    };

    const getStatusClass = ({ value }: { value: boolean }) => {
        return value ? 'bg-green-200' : 'bg-amber-500';
    };

    const formatStatus = ({ value }: { value: boolean }) => {
        return value ? 'processed' : 'pending';
    };

    const rows = data?.data ?? data ?? [];
    const rowCount = data?.count ?? 0;
    const d = detailQuery.data;
    const snapshot = d?.request_snapshot;
    const summary = snapshot?.summary;
    const rooms = snapshot?.rooms;

    return (
        <div className="space-y-4">
            <div className="flex lg:flex-row flex-col lg:space-x-8 lg:space-y-0 space-y-4 mb-8">
                <div className="w-full">
                    <Select
                        label=""
                        options={['pending', 'processed']}
                        value={selectType}
                        onChange={handleSelectChange}
                        multiple={false}
                    />
                </div>
            </div>

            <div className="w-full relative shadow-md rounded overflow-hidden" style={{ height: 800 }}>
                <AgGridReact
                    rowData={rows}
                    overlayNoRowsTemplate={isLoading ? '<span></span>' : '<span>Нет данных</span>'}
                    defaultColDef={{
                        flex: 1,
                        sortable: true,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: { suppressFilterButton: true }
                    }}
                    onRowClicked={goToDetail}
                >
                    <AgGridColumn headerName="Дата создания" field="created_datetime" sort="desc" valueFormatter={formatDate} filter />
                    <AgGridColumn headerName="Игрок" field="user_username" filter />
                    <AgGridColumn headerName="Статус" field="processed" valueFormatter={formatStatus} cellClass={getStatusClass} />
                </AgGridReact>
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.8)' }}>
                        <CircularProgress />
                    </div>
                ) : null}
            </div>

            <TablePagination
                component="div"
                count={rowCount}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(0); }}
            />

            <Dialog open={modalOpen} onClose={handleCloseModal} TransitionProps={{ onExited: () => setSelectedId(null) }} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: 'calc(100vh - 64px)', position: 'relative' } }}>
                <IconButton onClick={handleCloseModal} size="small" sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', pt: '16px !important' }}>
                    {detailQuery.isLoading ? (
                        <div className="flex items-center justify-center flex-1" style={{ minHeight: 400 }}>
                            <CircularProgress />
                        </div>
                    ) : d ? (<div className="flex flex-col flex-1">
                        <div className="flex flex-col lg:flex-row gap-x-16 items-start mb-8">
                            <div className="shrink-0">
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-xl font-medium">Запрос на расчёт №{selectedId}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                                    <p className="text-lg font-light">Дата создания:</p>
                                    <p className="text-lg font-light">{moment(d?.created_datetime).format('DD MMM YYYY HH:mm')}</p>
                                    <p className="text-lg font-light">Статус:</p>
                                    <p className="text-lg font-light"><span className={`px-2 rounded ${d?.processed ? 'bg-green-200' : 'bg-amber-500'}`} style={{ paddingTop: '1.2px', paddingBottom: '1.2px' }}>{d?.processed ? 'processed' : 'pending'}</span></p>
                                    <p className="text-lg font-light invisible">-</p>
                                    <p className="text-lg font-light invisible">-</p>
                                    <p className="text-lg font-light">Игрок:</p>
                                    <p className="text-lg font-light">{d?.user_username}</p>
                                    {summary && (<>
                                        <p className="text-lg font-light">ABI (30 дней):</p>
                                        <p className="text-lg font-light">${summary.abi_30days}</p>
                                        <p className="text-lg font-light">Выплата:</p>
                                        <p className="text-lg font-light">{summary.total_payout_percent}%</p>
                                    </>)}
                                </div>

                                {summary && (
                                    <div className="mt-8">
                                        <p className="text-lg font-medium mb-4">Пакет</p>
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                                            <p className="text-lg font-light">Результат пакета:</p>
                                            <p className="text-lg font-light">{summary.bundle?.result}</p>
                                            <p className="text-lg font-light">Результат игрока:</p>
                                            <p className="text-lg font-light">{summary.bundle?.player_result}</p>
                                            <p className="text-lg font-light">Профит SharkScope:</p>
                                            <p className="text-lg font-light">{summary.bundle?.sharkscope_profit}</p>
                                            <p className="text-lg font-light">Профит по балансам:</p>
                                            <p className="text-lg font-light">{summary.bundle?.balance_profit}</p>
                                            <p className="text-lg font-light">Профит NOS:</p>
                                            <p className="text-lg font-light">{summary.bundle?.nos_profit}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex-1">
                                {summary && (
                                    <div className="mb-8">
                                        <p className="text-xl font-medium mb-4">Контракт</p>
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-1" style={{ maxWidth: 'fit-content' }}>
                                            <p className="text-lg font-light">Профит SharkScope:</p>
                                            <p className="text-lg font-light">{summary.contract?.sharkscope_profit}</p>
                                            <p className="text-lg font-light">Профит по балансам:</p>
                                            <p className="text-lg font-light">{summary.contract?.balance_profit}</p>
                                            <p className="text-lg font-light">Профит NOS:</p>
                                            <p className="text-lg font-light">{summary.contract?.nos_profit}</p>
                                            <p className="text-lg font-light">Турниров всего:</p>
                                            <p className="text-lg font-light">{summary.contract?.entries_total}</p>
                                            <p className="text-lg font-light">Турниров отслежено:</p>
                                            <p className="text-lg font-light">{summary.contract?.entries_tracked}</p>
                                            <p className="text-lg font-light">Турниров NOS:</p>
                                            <p className="text-lg font-light">{summary.contract?.entries_nos}</p>
                                        </div>
                                    </div>
                                )}

                                {rooms && rooms.length > 0 && (
                                    <div>
                                        <p className="text-lg font-medium mb-4">Балансы по румам</p>
                                        <div className="bg-gray-50 border border-gray-200 rounded py-2" style={{ marginLeft: '-0.75rem', paddingLeft: '0.75rem', paddingRight: '5rem' }}>
                                            <table className="w-full text-left text-lg font-normal" style={{ borderSpacing: '0 14px', borderCollapse: 'separate' }}>
                                                <thead>
                                                    <tr className="border-b border-gray-300">
                                                        <th className="py-1 pr-4 font-semibold text-gray-600">Рум</th>
                                                        <th className="py-1 pr-4 font-semibold text-gray-600">Ник в руме</th>
                                                        <th className="py-1 pr-4 font-medium text-right text-gray-600" style={{ fontVariantNumeric: 'tabular-nums' }}>Баланс</th>
                                                        <th className="py-1 pr-4 font-semibold text-gray-600 text-right">Скриншот</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rooms.map((room: any, idx: number) => (
                                                        <tr key={room.account_record_id} className={idx % 2 === 0 ? 'bg-white/60' : ''}>
                                                            <td className="py-1 pr-4 text-gray-900">{room.room_name}</td>
                                                            <td className="py-1 pr-4 text-gray-900">{room.account_record_name}</td>
                                                            <td className="py-1 pr-4 text-right text-gray-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{room.balance}</td>
                                                            <td className="py-1 pr-4" style={{ textAlign: 'right' }}>
                                                                <div style={{ display: 'inline-flex', gap: 80, justifyContent: 'flex-end' }}>
                                                                    {[room.screenshot, room.screenshot2].filter(Boolean).map((url, i, arr) => (
                                                                        <ImageViewer
                                                                            key={i}
                                                                            url={url}
                                                                            label="Скрин"
                                                                            overlay={
                                                                                <>
                                                                                    <span>Рум: <b>{room.room_name}</b></span>
                                                                                    <span style={{ margin: '0 12px' }}>·</span>
                                                                                    <span>Ник в руме: <b>{room.account_record_name}</b></span>
                                                                                    <span style={{ margin: '0 12px' }}>·</span>
                                                                                    <span>Баланс: <b>{room.balance}</b></span>
                                                                                </>
                                                                            }
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {summary && d?.processed && (
                            <PayoutMessageBox username={d?.user_username} summary={summary} />
                        )}

                        {d?.data && !snapshot && (
                            <div className="mt-6 mb-4">
                                <p className="text-lg font-medium mb-4">Данные запроса</p>
                                <pre className="bg-gray-50 p-4 rounded text-sm overflow-auto max-h-96">
                                    {JSON.stringify(d?.data, null, 2)}
                                </pre>
                            </div>
                        )}

                        <div className="mt-auto pt-8 space-x-4 text-right">
                            {!d?.processed && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    loading={confirmMutation.isLoading}
                                    onClick={() => confirmMutation.mutate()}
                                >
                                    Подтвердить
                                </Button>
                            )}
                            {!d?.processed && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    Удалить
                                </Button>
                            )}
                        </div>
                    </div>) : null}
                </DialogContent>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                payoutRequestId={selectedId || ''}
                onClose={() => setDeleteDialogOpen(false)}
                onDeleted={() => {
                    queryClient.invalidateQueries(['payoutrequests']);
                    setDeleteDialogOpen(false);
                    setModalOpen(false);
                }}
            />
        </div>
    );
};

export default PayoutRequests;
